import React, { useState } from 'react';
import api from '../utils/api';
import '../styles/UpdateGoogleDrive.css';
import { escapeHtml } from '../utils/sanitize';

// File validation constants
const MAX_FILE_SIZE = 1024 * 1024; // 1MB
const ALLOWED_MIME_TYPES = ['application/json'];
const REQUIRED_JSON_FIELDS = ['type', 'project_id', 'private_key', 'client_email'];

const UpdateGoogleDrive = () => {
  const [jsonFile, setJsonFile] = useState(null);
  const [folderId, setFolderId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  // Validate JSON file content
  const validateJsonContent = (content) => {
    try {
      const json = JSON.parse(content);

      // Check required fields for Google service account
      const missingFields = REQUIRED_JSON_FIELDS.filter(field => !json[field]);
      if (missingFields.length > 0) {
        return { valid: false, error: `Missing required fields: ${missingFields.join(', ')}` };
      }

      // Validate service account type
      if (json.type !== 'service_account') {
        return { valid: false, error: 'Invalid JSON: type must be "service_account"' };
      }

      return { valid: true };
    } catch {
      return { valid: false, error: 'Invalid JSON format' };
    }
  };

  // Validate file
  const validateFile = async (file) => {
    // Check file extension
    if (!file.name.toLowerCase().endsWith('.json')) {
      return { valid: false, error: 'Only .json files are allowed' };
    }

    // Check MIME type
    if (!ALLOWED_MIME_TYPES.includes(file.type) && file.type !== '') {
      return { valid: false, error: 'Invalid file type. Only JSON files are allowed' };
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: 'File size exceeds 1MB limit' };
    }

    // Validate JSON content
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = validateJsonContent(e.target.result);
        resolve(result);
      };
      reader.onerror = () => resolve({ valid: false, error: 'Failed to read file' });
      reader.readAsText(file);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!jsonFile || !folderId.trim()) {
      setError('Please provide both JSON file and Folder ID.');
      return;
    }

    // Validate folder ID format (basic validation)
    const folderIdPattern = /^[a-zA-Z0-9_-]+$/;
    if (!folderIdPattern.test(folderId.trim())) {
      setError('Invalid Folder ID format');
      return;
    }

    setError('');
    setLoading(true);

    const formData = new FormData();
    formData.append('jsonFile', jsonFile);
    formData.append('folderId', escapeHtml(folderId.trim()));

    try {
      await api.post('/api/google-drive/update-config', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSuccess('Google service account updated successfully!');
      setJsonFile(null);
      setFolderId('');
      document.getElementById("file").value = "";
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setError('');
    setSuccess('');

    const validation = await validateFile(file);
    if (!validation.valid) {
      setError(validation.error);
      e.target.value = '';
      return;
    }

    setJsonFile(file);
  };

  const handleRemoveFile = (e) => {
    e.stopPropagation();
    setJsonFile(null);
    document.getElementById("file").value = "";
  };


  return (
    <div className='update-drive-main-container'>
      <h1 className='txt-center'>Update Google Drive Key and Google Drive Folder Id</h1>
      <div className='google-drive-container'>
        <form onSubmit={handleSubmit} className="update-form">
          <div className="form-group">
            <div
              className="container">
              <label
                htmlFor="file" style={{width:'100%'}}>
                {/* HEADER */}
                <div className="header" style={{ marginBottom: "8px" }}>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    width="32"
                    height="32"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7 10V9C7 6.23858 9.23858 4 12 4C14.7614 4 17 6.23858 17 9V10C19.2091 10 21 11.7909 21 14C21 15.4806 20.1956 16.8084 19 17.5M7 10C4.79086 10 3 11.7909 3 14C3 15.4806 3.8044 16.8084 5 17.5M7 10C7.43285 10 7.84965 10.0688 8.24006 10.1959M12 12V21M12 12L15 15M12 12L9 15"
                      stroke="#000000"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    ></path>
                  </svg>
                  <p style={{ fontWeight: "500", marginTop: "8px" }}>
                    Browse file to upload!
                  </p>
                </div>
              </label>
              {/* FOOTER */}
              <div className='footer'>
                <svg
                  fill="#000000"
                  viewBox="0 0 32 32"
                  width="28"
                  height="28"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M15.331 6H8.5v20h15V14.154h-8.169z"></path>
                  <path d="M18.153 6h-.009v5.342H23.5v-.002z"></path>
                </svg>

                <p style={{ margin: 0, fontSize: "14px" }}>
                  {jsonFile ? (
                    <span style={{ color: "#0a7", fontWeight: "600" }}>
                      {jsonFile.name}
                    </span>
                  ) : (
                    "No file selected"
                  )}
                </p>

                {jsonFile && (
                  <button
                    onClick={handleRemoveFile}
                    style={{
                      border: "none",
                      background: "transparent",
                      color: "red",
                      fontWeight: "bold",
                      cursor: "pointer",
                      fontSize: "16px",
                    }}
                    title="Remove file"
                  >
                    ✖
                  </button>
                )}
              </div>

              {/* HIDDEN INPUT */}
              <input
                id="file"
                type="file"
                accept=".json"
                onChange={handleFileChange}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  opacity: 0,
                  cursor: "pointer",
                }}
              />
            </div>
          </div>

      <div className="form-group">
        <label>Google Drive Folder ID</label>
        <input
          type="text"
          value={folderId}
          onChange={(e) => setFolderId(e.target.value)}
        />
      </div>

      {error && <p className="error-text">{error}</p>}
      {success && <p className="success-text">{success}</p>}

      <button
        type="submit"
        className="submit-btn"
        disabled={loading || !jsonFile || !folderId.trim()}
        style={{
          opacity: loading || !jsonFile || !folderId.trim() ? 0.6 : 1,
          cursor: loading || !jsonFile || !folderId.trim() ? 'not-allowed' : 'pointer'
        }}
      >
        {loading ? 'Updating...' : 'Update Google Drive Key & Folder Id'}
      </button>
    </form>
    <div className="guide-container">
      <h1>🔑 Google Drive API Setup Guide</h1>

      <Step title="Step 1: Create Google Cloud Project">
        <p>
          Visit <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer">Google Cloud Console</a> and create a new project.
        </p>
      </Step>

      <Step title="Step 2: Enable Google Drive API">
        <p>
          Go to <strong>APIs & Services &gt; Library</strong>, search for <strong>Google Drive API</strong>, and click <strong>Enable</strong>.
        </p>
      </Step>

      <Step title="Step 3: Create Service Account & Download JSON">
        <ol>
          <li>Go to <strong>IAM & Admin &gt; Service Accounts</strong>.</li>
          <li>Click <strong>Create Service Account</strong>.</li>
          <li>Assign a name and role (e.g., <code>Editor</code>).</li>
          <li>Click <strong>Done</strong>.</li>
          <li>Click the service account &gt; Go to <strong>Keys</strong> tab.</li>
          <li>Click <strong>Add Key &gt; Create New Key</strong>.</li>
          <li>Select <code>JSON</code> and download it.</li>
        </ol>
      </Step>

      <Step title="Step 4: Share Drive Folder">
        <ol>
          <li>Go to <a href="https://drive.google.com/" target="_blank" rel="noopener noreferrer">Google Drive</a>.</li>
          <li>Create a folder or use an existing one.</li>
          <li>Right-click &gt; <strong>Share</strong>.</li>
          <li>Paste the service account email (from JSON file).</li>
          <li>Give <strong>Editor</strong> access.</li>
        </ol>
      </Step>

      <Step title="Step 5: Get Folder ID">
        <p>
          Right-click the folder &gt; <strong>Get link</strong> and copy the URL:
        </p>
        <div className="code-box">
          https://drive.google.com/drive/folders/<strong>1AbCdEfGhIjKLMnOpQRstuVwXyZ</strong>
        </div>
        <p>The bold part is the <strong>Folder ID</strong>.</p>
      </Step>

      <Step title="Sample JSON Credentials">
        <pre className="code-box">
{`{
  "type": "service_account",
  "project_id": "your-project-id",
  "private_key_id": "abc123...",
  "private_key": "-----BEGIN PRIVATE KEY-----\\n...\\n-----END PRIVATE KEY-----\\n",
  "client_email": "service-account@your-project.iam.gserviceaccount.com",
  "client_id": "1234567890"
}`}
        </pre>
      </Step>
    </div>
      </div>
    </div>
  );
};


const Step = ({ title, children }) => (
  <div className="step-box">
    <h2>{title}</h2>
    <div>{children}</div>
  </div>
);

export default UpdateGoogleDrive;